// apps/widget/src/screens/consent.screen.tsx
//
// Consent before camera, always. The permission prompt must not fire until the
// visitor has read what is captured, that it is deleted, and who processes it.
//
// A permission dialog that appears before the explanation is both a compliance
// failure and the largest single drop-off in the funnel.

import { useEffect, useState } from 'react';
import { preloadFaceMesh } from '../lib/facemesh';
import { t } from '../lib/i18n';
import { useScan } from '../state';
import { SplitVisual } from '../components/split-visual';

export function ConsentScreen() {
  // The visitor has chosen to scan: fetch the face model while they read, so
  // the camera's guidance is ready the moment it opens.
  useEffect(() => preloadFaceMesh(), []);
  const { state, dispatch, go } = useScan();
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [analysisAgreed, setAnalysisAgreed] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [keepPhoto, setKeepPhoto] = useState(false);
  const retention = state.config.photoRetention;

  const clinic = state.config.tenant.name;
  const canContinue = ageConfirmed && analysisAgreed;

  return (
    <div className="gs-welcome">
      <div className="gs-welcome__copy gs-stack">
        <h1 className="gs-title">{t('consent.title')}</h1>

      <ul className="gs-facts">
        <li>{t('consent.what')}</li>
        <li>
          <strong>
            {retention?.offered ? t('consent.deletedUnlessKept', { clinic }) : t('consent.deleted')}
          </strong>
        </li>
        <li>{t('consent.processor')}</li>
      </ul>

      <label className="gs-check">
        <input
          type="checkbox"
          checked={ageConfirmed}
          onChange={(e) => setAgeConfirmed(e.target.checked)}
        />
        <span>{t('consent.age')}</span>
      </label>

      <label className="gs-check">
        <input
          type="checkbox"
          checked={analysisAgreed}
          onChange={(e) => setAnalysisAgreed(e.target.checked)}
        />
        <span>{t('consent.agree')}</span>
      </label>

      {/* Marketing consent is separate from biometric consent, and never
          bundled into it. A clinic cannot email scan leads without this. */}
      <label className="gs-check gs-check--muted">
        <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
        <span>{t('consent.marketing', { clinic })}</span>
      </label>

      {/* Keeping the photo is its own agreement: optional, unticked, never
          bundled with the analysis consent, and offered only when the clinic
          has switched photo keeping on. */}
      {retention?.offered && (
        <label className="gs-check gs-check--muted">
          <input type="checkbox" checked={keepPhoto} onChange={(e) => setKeepPhoto(e.target.checked)} />
          <span>{t('consent.keepPhoto', { clinic, days: String(retention.days) })}</span>
        </label>
      )}

      {/* The clinic's policy, not ours. They are the controller. */}
      {state.config.tenant.privacyPolicyUrl && (
        <a
          className="gs-link"
          href={state.config.tenant.privacyPolicyUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t('consent.privacy')} — {clinic}
        </a>
      )}

      <button
        className="gs-btn gs-btn--primary"
        disabled={!canContinue}
        onClick={() =>
          dispatch({ type: 'consent', marketingOptIn: marketing, retainPhoto: Boolean(retention?.offered) && keepPhoto })
        }
      >
        {t('consent.continue')}
      </button>
      </div>

      <SplitVisual config={state.config} />
    </div>
  );
}
